import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with default config
// NOTE: withCredentials is INTENTIONALLY false to keep CORS working with the
// Kubernetes ingress (which serves Access-Control-Allow-Origin: *). Auth is
// done via Authorization: Bearer <token> header (set by request interceptor).
// The backend still sets an httpOnly cookie as defense-in-depth, but the
// frontend doesn't rely on it.
const api = axios.create({
  baseURL: `${API}/api`,
  timeout: 20000, // 20 second timeout
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Request queue for retry logic
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - add auth token and cache busting
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add timestamp to prevent caching on GET requests
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      };
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors (robust version)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Never retry auth requests
    if (originalRequest?.url?.includes('/auth/')) {
      return Promise.reject(error);
    }
    
    // Handle 401 - ONLY clear token on explicit unauthorized, not on network errors
    if (error.response?.status === 401) {
      // Only clear if we actually got a 401 response from the server
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return Promise.reject(error);
    }
    
    // For network errors (no response), DON'T clear user session
    // Just reject and let the component handle it
    if (!error.response) {
      console.warn('Network error - keeping session active');
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

// Helper function to make API calls with optimistic updates
export const apiCall = async (method, url, data = null, options = {}) => {
  try {
    const config = {
      method,
      url,
      ...options,
    };
    
    if (data) {
      if (method === 'get') {
        config.params = data;
      } else {
        config.data = data;
      }
    }
    
    const response = await api(config);
    return { success: true, data: response.data };
  } catch (error) {
    const errorMessage = error.response?.data?.detail || error.message || 'Error de conexión';
    console.error(`API Error [${method.toUpperCase()} ${url}]:`, errorMessage);
    return { success: false, error: errorMessage };
  }
};

// Export both the instance and helper
export default api;
